import { setUser } from '@sentry/react';
import React, { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { ErrorSection } from 'src/components/sections/ErrorSection';
import { SplashScreen } from 'src/components/splashes/SplashScreen';
import { Button } from 'src/components/ui/button';
import { useLogin } from 'src/hooks/useLogin';
import { useUser } from 'src/hooks/useUser';
import { FullWidthConstrainedLayout } from 'src/layouts/FullWidthConstrainedLayout';

type AuthGuardProps = { children: ReactElement };

export function AuthGuard({ children }: AuthGuardProps): ReactElement {
  const { isLoading, isAuthenticated, error } = useAuth();
  const { login } = useLogin();
  const { t } = useTranslation('errors');
  const { user } = useUser();

  useEffect(() => {
    if (!(isLoading || isAuthenticated)) {
      login();
    }
  }, [isLoading, isAuthenticated, login]);

  useEffect(() => {
    if (user) {
      setUser({
        id: user.id,
        email: user.email,
        username: user.username,
      });
    } else {
      setUser(null);
    }
  }, [user]);

  return !error ? (
    isAuthenticated && !isLoading ? (
      children
    ) : (
      <SplashScreen />
    )
  ) : (
    <div className='flex h-screen w-screen overflow-hidden'>
      <div className='flex grow flex-col overflow-y-auto'>
        <FullWidthConstrainedLayout>
          <ErrorSection
            code={401}
            title={t('Unauthorized')}
            description={t('You need to be logged in to access this page')}
            action={<Button onClick={() => login()}>{t('Login')}</Button>}
          />
        </FullWidthConstrainedLayout>
      </div>
    </div>
  );
}
